import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { InfoIcon } from 'ui-components/components/info-icon';
import { Input } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';
import { SimpleDropdown } from '../../../../../../../components/simple-dropdown';
import type { ProductDangerousGoods } from '../../../../../use-product-details-context';
import { useProductDetailsContext } from '../../../../../use-product-details-context';
import {
  HAZARD_CLASS_OPTIONS,
  HAZMAT_ADDITIONAL_DETAILS_MAX_LENGTH,
  HAZMAT_EMERGENCY_CONTACT_NAME_MAX_LENGTH,
  HAZMAT_EMERGENCY_PHONE_MAX_LENGTH,
  HAZMAT_ERG_REFERENCE_MAX_LENGTH,
  HAZMAT_FLASHPOINT_MAX_TEMP_F,
  HAZMAT_SHIPPING_NAME_MAX_LENGTH,
  HAZMAT_UN_NA_MAX_LENGTH,
  PACKING_GROUP_OPTIONS,
} from './hazmat-constants';
import { messages } from './messages';
import styles from './hazmat-section.module.css';

export const HazmatSection = () => {
  const intl = useIntl();
  const { productDangerousGoods, setProductDangerousGoods } = useProductDetailsContext();
  const dg = useMemo(() => (productDangerousGoods ?? {}) as ProductDangerousGoods, [productDangerousGoods]);

  const updateField = useCallback(
    (property: keyof ProductDangerousGoods) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setProductDangerousGoods({ ...dg, [property]: event.target.value });
    },
    [setProductDangerousGoods, dg],
  );

  const updateFieldDirect = useCallback(
    (property: keyof ProductDangerousGoods) => (value: string) => {
      setProductDangerousGoods({ ...dg, [property]: value });
    },
    [setProductDangerousGoods, dg],
  );

  const onFlashpointChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      const num = Number(raw);
      const clamped = raw === '' || isNaN(num) ? raw : String(Math.min(num, HAZMAT_FLASHPOINT_MAX_TEMP_F));
      setProductDangerousGoods({ ...dg, flashpointTemperatureF: clamped });
    },
    [setProductDangerousGoods, dg],
  );

  const onHazardClassSelected = useCallback(
    (value: string) => {
      const parts = value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      setProductDangerousGoods({
        ...dg,
        productClass: parts[0] ?? '',
        productClassSubsidiary: parts.slice(1).join(', '),
      });
    },
    [setProductDangerousGoods, dg],
  );

  const selectedHazardClassKey = [dg.productClass, dg.productClassSubsidiary].filter(Boolean).join(', ');

  const onPackagingGroupSelected = useCallback(
    (packagingGroup: string) => setProductDangerousGoods({ ...dg, packagingGroup: packagingGroup as 'i' | 'ii' | 'iii' }),
    [setProductDangerousGoods, dg],
  );

  const shippingNameLength = dg.shippingName?.length ?? 0;
  const additionalDetailsLength = dg.additionalDetails?.length ?? 0;

  return (
    <div className={styles.section}>
      {/* Hazardous material details */}
      <div>
        <Text element='div' textType={TextType.Label} value={messages.hazardousMaterialDetails} />
      </div>

      {/* 4-col: UN Number | NA Number | Hazard Class | Packing Group */}
      <div className={styles['four-col']}>
        <div className={styles.field}>
          <Text htmlFor='hazmat-un-number' textType={TextType.Label} value={messages.unNumber} />
          <Input
            id='hazmat-un-number'
            maxLength={HAZMAT_UN_NA_MAX_LENGTH}
            onChange={updateField('idNumber')}
            value={dg.idNumber ?? ''}
            version='version2'
          />
        </div>
        <div className={styles.field}>
          <Text htmlFor='hazmat-na-number' textType={TextType.Label} value={messages.naNumber} />
          <Input
            id='hazmat-na-number'
            maxLength={HAZMAT_UN_NA_MAX_LENGTH}
            onChange={updateField('naNumber')}
            value={dg.naNumber ?? ''}
            version='version2'
          />
        </div>
        <div className={styles.field}>
          <Text htmlFor='hazmat-hazard-class' textType={TextType.Label} value={messages.hazardClass} />
          <SimpleDropdown
            defaultTitle={<FormattedMessage {...messages.selectAnOption} />}
            dropdownMenuShouldHaveWidthOfButton
            items={HAZARD_CLASS_OPTIONS}
            onItemSelected={onHazardClassSelected}
            selectedItemKey={selectedHazardClassKey}
          />
        </div>
        <div className={styles.field}>
          <Text htmlFor='hazmat-packing-group' textType={TextType.Label} value={messages.packingGroup} />
          <SimpleDropdown
            defaultTitle={<FormattedMessage {...messages.selectAnOption} />}
            dropdownMenuShouldHaveWidthOfButton
            items={PACKING_GROUP_OPTIONS}
            onItemSelected={onPackagingGroupSelected}
            selectedItemKey={dg.packagingGroup}
          />
        </div>
      </div>

      {/* Full-width: Proper Shipping Name with char count */}
      <div>
        <Text htmlFor='hazmat-shipping-name' textType={TextType.Label} value={messages.properShippingName} />
        <Input
          id='hazmat-shipping-name'
          maxLength={HAZMAT_SHIPPING_NAME_MAX_LENGTH}
          onChange={updateField('shippingName')}
          value={dg.shippingName ?? ''}
          version='version2'
        />
        <div className={styles['char-count']}>
          {shippingNameLength}/{HAZMAT_SHIPPING_NAME_MAX_LENGTH}
        </div>
      </div>

      {/* 2-col: ERG Reference No. | Flashpoint Temperature */}
      <div className={styles['two-col']}>
        <div className={styles.field}>
          <div className={styles['label-with-icon']}>
            <Text htmlFor='hazmat-erg' textType={TextType.Label} value={messages.ergReferenceNumber} />
            <InfoIcon tooltipText={intl.formatMessage(messages.ergReferenceNumberTooltip)} />
          </div>
          <Input
            id='hazmat-erg'
            maxLength={HAZMAT_ERG_REFERENCE_MAX_LENGTH}
            onChange={updateField('ergReferenceNumber')}
            value={dg.ergReferenceNumber ?? ''}
            version='version2'
          />
        </div>
        <div className={styles.field}>
          <Text htmlFor='hazmat-flashpoint' textType={TextType.Label} value={messages.flashpointTemperatureF} />
          <Input
            id='hazmat-flashpoint'
            inputMode='numeric'
            onChange={onFlashpointChange}
            pattern='-?[0-9]*'
            type='number'
            value={dg.flashpointTemperatureF ?? ''}
            version='version2'
            max={HAZMAT_FLASHPOINT_MAX_TEMP_F}
          />
        </div>
      </div>

      {/* Full-width: Additional Details textarea with char count */}
      <div>
        <Text htmlFor='hazmat-additional-details' textType={TextType.Label} value={messages.additionalDetails} />
        <FloatTextArea
          id='hazmat-additional-details'
          maxLength={HAZMAT_ADDITIONAL_DETAILS_MAX_LENGTH}
          onChange={updateFieldDirect('additionalDetails')}
          placeholder={intl.formatMessage(messages.additionalDetailsPlaceholder)}
          value={dg.additionalDetails ?? ''}
        />
        <div className={styles['char-count']}>
          {additionalDetailsLength}/{HAZMAT_ADDITIONAL_DETAILS_MAX_LENGTH}
        </div>
      </div>

      <hr className={styles.separator} />

      {/* 24-hour emergency contact */}
      <div>
        <Text element='div' textType={TextType.Label} value={messages.emergencyContactTitle} />
      </div>
      <div className={styles['two-col']}>
        <div className={styles.field}>
          <Text htmlFor='hazmat-emergency-name' textType={TextType.Label} value={messages.emergencyContactName} />
          <Input
            id='hazmat-emergency-name'
            maxLength={HAZMAT_EMERGENCY_CONTACT_NAME_MAX_LENGTH}
            onChange={updateField('emergencyContactName')}
            value={dg.emergencyContactName ?? ''}
            version='version2'
          />
        </div>
        <div className={styles.field}>
          <Text htmlFor='hazmat-emergency-phone' textType={TextType.Label} value={messages.emergencyContactPhone} />
          <Input
            id='hazmat-emergency-phone'
            inputMode='numeric'
            maxLength={HAZMAT_EMERGENCY_PHONE_MAX_LENGTH}
            onChange={updateField('emergencyContactPhone')}
            pattern='[0-9]*'
            type='tel'
            value={dg.emergencyContactPhone ?? ''}
            version='version2'
          />
        </div>
      </div>

      {/* Footer */}
      <Text className={styles['footer-caption']} element='div' textType={TextType.Caption} value={messages.footerCaption} />
    </div>
  );
};
